(() => {
  const MCGILL_PRODUCT_IDS = new Set([
    '11081302573249',
    '11081304670401',
    '11085742702785',
    '11085780713665'
  ]);

  const MCGILL_VARIANT_IDS = new Set([
    '50621708927169', '50621721575617',
    '50614745202881', '50614745235649', '50614745268417', '50614745301185',
    '50621600563393', '50621600465089', '50621600497857', '50621600530625',
    '50621758472385', '50621758505153'
  ]);

  const ADD_MESSAGE = 'McGill Convocation products must be ordered separately from regular store items. Please complete or clear your current cart before adding this item.';
  const MIXED_MESSAGE = 'McGill Convocation products must be ordered separately from regular store items. Please remove either the McGill items or the regular store items before checkout.';
  const originalFetch = window.fetch.bind(window);
  let refreshTimer;

  const normalizeId = (value) => String(value ?? '').trim();
  const isMcGillVariant = (id) => MCGILL_VARIANT_IDS.has(normalizeId(id));

  function itemIsMcGill(item) {
    return MCGILL_PRODUCT_IDS.has(normalizeId(item.product_id)) ||
      MCGILL_VARIANT_IDS.has(normalizeId(item.variant_id || item.id));
  }

  function cartTypes(cart) {
    let hasMcGill = false;
    let hasRegular = false;
    (cart?.items || []).forEach((item) => {
      if (itemIsMcGill(item)) hasMcGill = true;
      else hasRegular = true;
    });
    return { hasMcGill, hasRegular, mixed: hasMcGill && hasRegular };
  }

  function incomingTypes(ids) {
    let hasMcGill = false;
    let hasRegular = false;
    ids.filter(Boolean).forEach((id) => {
      if (isMcGillVariant(id)) hasMcGill = true;
      else hasRegular = true;
    });
    return { hasMcGill, hasRegular, mixed: hasMcGill && hasRegular };
  }

  function conflicts(cart, ids) {
    if (!ids.length) return false;
    const current = cartTypes(cart);
    const incoming = incomingTypes(ids);
    return incoming.mixed ||
      (current.hasMcGill && incoming.hasRegular) ||
      (current.hasRegular && incoming.hasMcGill);
  }

  async function getCart() {
    const response = await originalFetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  }

  function showToast(message) {
    let toast = document.getElementById('mcgill-cart-guard-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'mcgill-cart-guard-toast';
      toast.setAttribute('role', 'alert');
      Object.assign(toast.style, {
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: '2147483647',
        width: 'min(560px, calc(100% - 32px))',
        padding: '14px 18px',
        border: '1px solid #b42318',
        borderRadius: '8px',
        background: '#fff4f2',
        color: '#7a271a',
        fontSize: '15px',
        lineHeight: '1.4',
        boxShadow: '0 8px 30px rgba(0,0,0,.16)'
      });
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    clearTimeout(toast._mcgillTimer);
    toast._mcgillTimer = setTimeout(() => toast.remove(), 8000);
  }

  function idsFromFormData(formData) {
    const ids = [];
    for (const [key, value] of formData.entries()) {
      if (key === 'id' || /^items\[\d+\]\[id\]$/.test(key)) ids.push(normalizeId(value));
    }
    return ids.filter(Boolean);
  }

  function idsFromObject(value) {
    const ids = [];
    if (!value || typeof value !== 'object') return ids;
    if (value.id != null) ids.push(normalizeId(value.id));
    if (Array.isArray(value.items)) {
      value.items.forEach((item) => {
        if (item?.id != null) ids.push(normalizeId(item.id));
      });
    }
    return ids.filter(Boolean);
  }

  function idsFromBody(body) {
    if (!body) return [];
    if (body instanceof FormData) return idsFromFormData(body);
    if (body instanceof URLSearchParams) {
      const data = new FormData();
      body.forEach((value, key) => data.append(key, value));
      return idsFromFormData(data);
    }
    if (typeof body === 'string') {
      try {
        return idsFromObject(JSON.parse(body));
      } catch (_) {
        try {
          const params = new URLSearchParams(body);
          const data = new FormData();
          params.forEach((value, key) => data.append(key, value));
          return idsFromFormData(data);
        } catch (_) {
          return [];
        }
      }
    }
    return idsFromObject(body);
  }

  async function idsFromFetch(input, init) {
    if (init?.body) return idsFromBody(init.body);
    if (input instanceof Request) {
      try {
        const text = await input.clone().text();
        return idsFromBody(text);
      } catch (_) {
        return [];
      }
    }
    return [];
  }

  function endpointMatches(input, action) {
    try {
      const raw = input instanceof Request ? input.url : input;
      const pathname = new URL(raw, window.location.origin).pathname;
      return pathname.includes(`/cart/${action}`);
    } catch (_) {
      return false;
    }
  }

  function isCartContext(element) {
    if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) return true;
    return Boolean(element.closest('.mini-cart, cart-drawer, [id="MiniCart"], [id*="mini-cart" i], form#cart'));
  }

  async function refreshCheckoutGuard() {
    let cart;
    try {
      cart = await getCart();
    } catch (_) {
      return;
    }

    const { mixed } = cartTypes(cart);
    const checkoutButtons = [...document.querySelectorAll('button[name="checkout"], input[name="checkout"]')]
      .filter(isCartContext);

    document.querySelectorAll('#mcgill-mixed-cart-alert').forEach((alert) => {
      if (!mixed) alert.remove();
    });

    checkoutButtons.forEach((button) => {
      if (mixed) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.mcgillGuardDisabled = 'true';
      } else if (button.dataset.mcgillGuardDisabled === 'true') {
        button.disabled = cart.item_count === 0;
        button.removeAttribute('aria-disabled');
        delete button.dataset.mcgillGuardDisabled;
      }
    });

    document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons').forEach((container) => {
      if (!isCartContext(container)) return;
      if (mixed) {
        if (!container.dataset.mcgillGuardDisplay) container.dataset.mcgillGuardDisplay = container.style.display || '';
        container.style.display = 'none';
        container.dataset.mcgillGuardHidden = 'true';
      } else if (container.dataset.mcgillGuardHidden === 'true') {
        container.style.display = container.dataset.mcgillGuardDisplay || '';
        delete container.dataset.mcgillGuardHidden;
        delete container.dataset.mcgillGuardDisplay;
      }
    });

    if (mixed && checkoutButtons.length) {
      const firstButton = checkoutButtons[0];
      const existing = document.getElementById('mcgill-mixed-cart-alert');
      if (!existing) {
        const alert = document.createElement('div');
        alert.id = 'mcgill-mixed-cart-alert';
        alert.setAttribute('role', 'alert');
        alert.textContent = MIXED_MESSAGE;
        Object.assign(alert.style, {
          margin: '0 0 14px',
          padding: '12px 14px',
          border: '1px solid #b42318',
          borderRadius: '6px',
          background: '#fff4f2',
          color: '#7a271a',
          fontSize: '14px',
          lineHeight: '1.4'
        });
        const target = firstButton.closest('.button-container, .cart__ctas, .cart__footer') || firstButton.parentElement;
        if (target) target.insertBefore(alert, target.firstChild);
        else firstButton.before(alert);
      }
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCheckoutGuard, 150);
    setTimeout(refreshCheckoutGuard, 650);
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    let action;
    try {
      action = new URL(form.action || window.location.href, window.location.origin).pathname;
    } catch (_) {
      return;
    }
    if (!action.includes('/cart/add')) return;

    if (form.dataset.mcgillGuardApproved === 'true') {
      delete form.dataset.mcgillGuardApproved;
      return;
    }

    const ids = idsFromFormData(new FormData(form));
    if (!ids.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      const cart = await getCart();
      if (conflicts(cart, ids)) {
        showToast(ADD_MESSAGE);
        return;
      }
      form.dataset.mcgillGuardApproved = 'true';
      form.requestSubmit(event.submitter || undefined);
    } catch (_) {
      form.dataset.mcgillGuardApproved = 'true';
      form.requestSubmit(event.submitter || undefined);
    }
  }, true);

  document.addEventListener('click', async (event) => {
    const addControl = event.target.closest?.('add-to-cart[data-variant-id]');
    if (!addControl) return;

    if (addControl.dataset.mcgillGuardApproved === 'true') {
      delete addControl.dataset.mcgillGuardApproved;
      return;
    }

    const id = normalizeId(addControl.dataset.variantId);
    if (!id) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      const cart = await getCart();
      if (conflicts(cart, [id])) {
        showToast(ADD_MESSAGE);
        return;
      }
      addControl.dataset.mcgillGuardApproved = 'true';
      addControl.click();
    } catch (_) {
      addControl.dataset.mcgillGuardApproved = 'true';
      addControl.click();
    }
  }, true);

  window.fetch = async function(input, init) {
    if (endpointMatches(input, 'add')) {
      const ids = await idsFromFetch(input, init);
      if (ids.length) {
        try {
          const cart = await getCart();
          if (conflicts(cart, ids)) {
            showToast(ADD_MESSAGE);
            return new Response(JSON.stringify({
              status: 422,
              message: 'Cart Error',
              description: ADD_MESSAGE
            }), {
              status: 422,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        } catch (_) {
          // If the cart can't be read, leave Shopify's normal add-to-cart flow untouched.
        }
      }
    }

    const response = await originalFetch(input, init);
    if (endpointMatches(input, 'add') || endpointMatches(input, 'change') || endpointMatches(input, 'update') || endpointMatches(input, 'clear')) {
      scheduleRefresh();
    }
    return response;
  };

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest?.('button[name="checkout"], input[name="checkout"]');
    if (checkout?.dataset.mcgillGuardDisabled === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      showToast(MIXED_MESSAGE);
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshCheckoutGuard, { once: true });
  } else {
    refreshCheckoutGuard();
  }

  const observer = new MutationObserver(() => scheduleRefresh());
  const observe = () => {
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.body) observe();
  else document.addEventListener('DOMContentLoaded', observe, { once: true });
})();
